import type { ModelsResolver } from "@/models/resolvers";
import { revisionDefaultFields } from "@/models/resolvers";
import { useLogger } from "@/composables/logger";
import { resolveNestedList } from "@/composables/cache";
import { postgresToJsonArray } from "@/utils/utils-dependent";

const { info: logInfo } = useLogger("SCENARIOS_EQUIPMENT_RESOLVER");

export default {
  queries: {
    scenario_equipment: {
      mle: (parent, args, cache, info) => {
        return parent.mle_id
          ? { __typename: "motor_lead_extensions", id: parent.mle_id }
          : null;
      },
      lower_mle: (parent, args, cache, info) => {
        return parent.lower_mle_id
          ? { __typename: "motor_lead_extensions", id: parent.lower_mle_id }
          : null;
      },
      motor: (parent, args, cache, info) => {
        return parent.motor_id
          ? { __typename: "motors", id: parent.motor_id }
          : null;
      },
      lower_motor: (parent, args, cache, info) => {
        return parent.lower_motor_id
          ? { __typename: "motors", id: parent.lower_motor_id }
          : null;
      },
      bolt_on_discharge: (parent, args, cache, info) => {
        return parent.bolt_on_discharge_id
          ? {
              __typename: "bolt_on_discharges",
              id: parent.bolt_on_discharge_id
            }
          : null;
      },
      lower_bolt_on_discharge: (parent, args, cache, info) => {
        return parent.lower_bolt_on_discharge_id
          ? {
              __typename: "bolt_on_discharges",
              id: parent.lower_bolt_on_discharge_id
            }
          : null;
      },
      shroud: (parent, args, cache, info) => {
        return parent.shroud_id
          ? { __typename: "casings", id: parent.shroud_id }
          : null;
      },
      lower_shroud: (parent, args, cache, info) => {
        return parent.lower_shroud_id
          ? { __typename: "casings", id: parent.lower_shroud_id }
          : null;
      },
      sensor: (parent, args, cache, info) => {
        return parent.sensor_id
          ? { __typename: "sensors", id: parent.sensor_id }
          : null;
      },
      lower_sensor: (parent, args, cache, info) => {
        return parent.lower_sensor_id
          ? { __typename: "sensors", id: parent.lower_sensor_id }
          : null;
      },
      vsd: (parent, args, cache, info) => {
        return parent.vsd_id
          ? { __typename: "variable_speed_drives", id: parent.vsd_id }
          : null;
      },
      switchboard: (parent, args, cache, info) => {
        return parent.switchboard_id
          ? { __typename: "switchboards", id: parent.switchboard_id }
          : null;
      },
      step_down_transformer: (parent, args, cache, info) => {
        return parent.down_trans_id
          ? {
              __typename: "step_down_transformers",
              id: parent.down_trans_id
            }
          : null;
      },
      step_up_transformer: (parent, args, cache, info) => {
        return parent.up_trans_id
          ? {
              __typename: "step_up_transformers",
              id: parent.up_trans_id
            }
          : null;
      },
      seal_motor_oil: (parent, args, cache, info) => {
        return parent.seal_motor_oil_id
          ? { __typename: "seal_motor_oil", id: parent.seal_motor_oil_id }
          : null;
      },
      lower_seal_motor_oil: (parent, args, cache, info) => {
        return parent.lower_seal_motor_oil_id
          ? { __typename: "seal_motor_oil", id: parent.lower_seal_motor_oil_id }
          : null;
      },
      uphole_y_tool: (parent, args, cache, info) => {
        return parent.uphole_y_tool_id
          ? { __typename: "y_tools", id: parent.uphole_y_tool_id }
          : null;
      },
      downhole_y_tool: (parent, args, cache, info) => {
        return parent.downhole_y_tool_id
          ? { __typename: "y_tools", id: parent.downhole_y_tool_id }
          : null;
      },
      xref_intakes: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("xref_intakes", args) || "xref_intakes",
          "scenario_equipment_xref_intakes"
        );
      },
      lower_xref_intakes: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("lower_xref_intakes", args) || "lower_xref_intakes",
          "scenario_equipment_xref_intakes"
        );
      },
      xref_seals: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("xref_seals", args) || "xref_seals",
          "scenario_equipment_xref_seals"
        );
      },
      lower_xref_seals: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("lower_xref_seals", args) || "lower_xref_seals",
          "scenario_equipment_xref_seals"
        );
      },
      xref_pumps: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("xref_pumps", args) || "xref_pumps",
          "scenario_equipment_xref_pumps"
        );
      },
      lower_xref_pumps: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("lower_xref_pumps", args) || "lower_xref_pumps",
          "scenario_equipment_xref_pumps"
        );
      },
      xref_cables: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("xref_cables", args) || "xref_cables",
          "scenario_equipment_xref_cables"
        );
      },
      lower_xref_cables: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("lower_xref_cables", args) || "lower_xref_cables",
          "scenario_equipment_xref_cables"
        );
      },
      uphole_xref_y_tool_bypass_tubings: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("uphole_xref_y_tool_bypass_tubings", args) ||
            "uphole_xref_y_tool_bypass_tubings",
          "sc_equip_xref_uphole_ytool_bypass_tubings"
        );
      },
      uphole_xref_y_tool_equipment_tubings: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("uphole_xref_y_tool_equipment_tubings", args) ||
            "uphole_xref_y_tool_equipment_tubings",
          "sc_equip_xref_uphole_ytool_equip_tubings"
        );
      },
      downhole_xref_y_tool_bypass_tubings: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("downhole_xref_y_tool_bypass_tubings", args) ||
            "downhole_xref_y_tool_bypass_tubings",
          "sc_equip_xref_downhole_ytool_bypass_tubings"
        );
      },
      downhole_xref_y_tool_equipment_tubings: (parent, args, cache, info) => {
        return resolveNestedList(
          parent.id,
          cache,
          "scenario_equipment",
          cache.keyOfField("downhole_xref_y_tool_equipment_tubings", args) ||
            "downhole_xref_y_tool_equipment_tubings",
          "sc_equip_xref_downhole_ytool_equip_tubings"
        );
      }
    }
  },
  optimistic: {
    insert_revisions_scenario_equipment_revisions(vars, cache, info) {
      logInfo("Optimistic", { vars, cache, info });
      const affectedRows = vars.objects.length;

      return {
        affected_rows: affectedRows,
        __typename: "revisions_scenario_equipment_revisions_mutation_response",
        returning: vars.objects.map(obj => ({
          __typename: "revisions_scenario_equipment_revisions",
          id: obj.id,
          scenario_equipment_view: {
            __typename: "scenario_equipment",
            ...obj,
            ...revisionDefaultFields(obj),
            seals_order: postgresToJsonArray(
              obj.seals_order as unknown as string
            ),
            lower_seals_order: postgresToJsonArray(
              obj.lower_seals_order as unknown as string
            ),
            intakes_order: postgresToJsonArray(
              obj.intakes_order as unknown as string
            ),
            lower_intakes_order: postgresToJsonArray(
              obj.lower_intakes_order as unknown as string
            ),
            cables_order: postgresToJsonArray(
              obj.cables_order as unknown as string
            ),
            lower_cables_order: postgresToJsonArray(
              obj.lower_cables_order as unknown as string
            ),
            pumps_order: postgresToJsonArray(
              obj.pumps_order as unknown as string
            ),
            lower_pumps_order: postgresToJsonArray(
              obj.lower_pumps_order as unknown as string
            ),
            uphole_y_tool_bypass_tubing_order: postgresToJsonArray(
              obj.uphole_y_tool_bypass_tubing_order as unknown as string
            ),
            uphole_y_tool_equipment_tubing_order: postgresToJsonArray(
              obj.uphole_y_tool_equipment_tubing_order as unknown as string
            ),
            downhole_y_tool_bypass_tubing_order: postgresToJsonArray(
              obj.downhole_y_tool_bypass_tubing_order as unknown as string
            ),
            downhole_y_tool_equipment_tubing_order: postgresToJsonArray(
              obj.downhole_y_tool_equipment_tubing_order as unknown as string
            )
          }
        }))
      };
    },
    insert_revisions_scenario_equipment_revisions_one(vars, cache, info) {
      logInfo("Optimistic", { vars, cache, info });
      return {
        id: vars.object.id,
        __typename: "revisions_scenario_equipment_revisions",
        scenario_equipment_view: {
          __typename: "scenario_equipment",
          ...vars.object,
          ...revisionDefaultFields(vars.object),
          seals_order: postgresToJsonArray(
            vars.object.seals_order as unknown as string
          ),
          lower_seals_order: postgresToJsonArray(
            vars.object.lower_seals_order as unknown as string
          ),
          intakes_order: postgresToJsonArray(
            vars.object.intakes_order as unknown as string
          ),
          lower_intakes_order: postgresToJsonArray(
            vars.object.lower_intakes_order as unknown as string
          ),
          cables_order: postgresToJsonArray(
            vars.object.cables_order as unknown as string
          ),
          lower_cables_order: postgresToJsonArray(
            vars.object.lower_cables_order as unknown as string
          ),
          pumps_order: postgresToJsonArray(
            vars.object.pumps_order as unknown as string
          ),
          lower_pumps_order: postgresToJsonArray(
            vars.object.lower_pumps_order as unknown as string
          ),
          uphole_y_tool_bypass_tubing_order: postgresToJsonArray(
            vars.object.uphole_y_tool_bypass_tubing_order as unknown as string
          ),
          uphole_y_tool_equipment_tubing_order: postgresToJsonArray(
            vars.object
              .uphole_y_tool_equipment_tubing_order as unknown as string
          ),
          downhole_y_tool_bypass_tubing_order: postgresToJsonArray(
            vars.object.downhole_y_tool_bypass_tubing_order as unknown as string
          ),
          downhole_y_tool_equipment_tubing_order: postgresToJsonArray(
            vars.object
              .downhole_y_tool_equipment_tubing_order as unknown as string
          )
        }
      };
    }
  }
} as ModelsResolver;
